import React, { useState, useEffect, useCallback } from "react";
import { Modal } from "react-bootstrap";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import GenericTable from "../../components/0. Tablas/PerfilesTable";
import MaestrasView from "../../components/1.1 maestras/Maestra";
import LoadingOverlay from "../../components/0. Utilidades/LoadingOverlay";
import { FaWhatsapp } from "react-icons/fa";
import { getApiUrl } from "../../utils/apiUrl";

const MaestraPage = () => {
  const { rut } = useParams();
  const [selectedMaestra, setSelectedMaestra] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [currentPageSize, setCurrentPageSize] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const navigate = useNavigate();
  const handlePageChange = useCallback((newPage) => {
    setCurrentPageIndex(newPage);
  }, []);

  const handlePageSizeChange = useCallback((newSize) => {
    setCurrentPageSize(newSize);
  }, []);

  const location = useLocation();

  // 1) Map route -> backend Maestra.tipo values
  const tipoByPath = {
    "/todas": "Todas",
    "/comunidad": "Maestra",
    "/maestras": "Maestra",
    "/maestras_externas": "Maestra Externa",
    "/alumna": "Alumna",
    "/especial": "Especial",
  };

  // 2) Normalize path (remove trailing slash)
  const normalizedPath = location.pathname.replace(/\/$/, "");
  const basePath = `/${normalizedPath.split("/")[1] || "maestras"}`;

  // 3) Pick tipo (default to "Maestra" if unknown)
  let tipo;

  if (basePath === "/todas") {
    // Send all tipos except "Todas"
    tipo = Object.values(tipoByPath)
      .filter((t) => t !== "Todas")
      .join(",");
  } else {
    tipo = tipoByPath[basePath] || "Maestra";
  }

  const requiresWhatsappGroup = basePath === "/comunidad";

  useEffect(() => {
    if (rut) {
      setLoadingDetail(true);
      fetch(getApiUrl(`api/perfiles/maestras/${rut}/`))
        .then((response) => response.json())
        .then((data) => {
          setSelectedMaestra(data);
          setShowModal(true); // <== ADD THIS LINE
          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching maestra:", error);
          setLoading(false);
        })
        .finally(() => {
          setLoadingDetail(false);
        });
    } else {
      setLoading(false);
    }
  }, [rut]);

  const handleMaestraSelect = (maestra) => {
    setLoadingDetail(true);
    setShowModal(false);
    setSelectedMaestra(null);
    navigate(`${basePath}/${maestra.rut}`);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedMaestra(null);
    navigate(basePath);
  };

  const columns = [
  {
    Header: "Maestra",
    accessor: "nombre", // still needed for sorting/search
    Cell: ({ row }) => {
      const item = row.original;

      const groups = Array.isArray(item?.grupos_whatsapp)
        ? item.grupos_whatsapp
        : [];

      const groupNames = groups
        .map((g) => g?.nombre)
        .filter(Boolean);

      const relation = (item?.e_relacion || "").toLowerCase();

      return (
        <div className="maestra-row">
          
          {/* Vertical relation bar */}
          <div
            className={`maestra-relation-bar ${
              relation === "problema"
                ? "maestra-relation-bar-problema"
                : "maestra-relation-bar-ok"
            }`}
          />

          {/* Main content */}
          <div className="maestra-content">

            {/* Top line */}
            <div className="maestra-main-line">

              <span className="maestra-name">
                {item?.nombre ?? ""}
              </span>

              {item?.sec === true && (
                <span className="badge bg-info">SEC</span>
              )}

              {item?.lista_negra === true && (
                <span
                  className="maestra-lista-negra-badge"
                  title="Perfil marcado como no prioritario para análisis"
                >
                  No prioritaria
                </span>
              )}

              {groupNames.length > 0 && (
                <span className="maestra-whatsapp-wrapper">
                  <FaWhatsapp className="maestra-whatsapp-icon" />

                  <span className="maestra-whatsapp-tooltip">
                    {groupNames.map((name) => (
                      <span key={name}>{name}</span>
                    ))}
                  </span>
                </span>
              )}
            </div>

            {/* Secondary info */}
            <div className="maestra-secondary">
              <span>{item?.comuna}</span>
              <span className="separator">|</span>
              <span>{item?.oficio}</span>
            </div>

            

          </div>
        </div>
      );
    },
  },
];


  if (loading) return <LoadingOverlay />;

  const titleLabel =
    basePath === "/todas"
      ? "Todas"
      : requiresWhatsappGroup
        ? "Comunidad"
        : tipo;

  const tableApiUrl = new URL(
    getApiUrl("api/perfiles/tabla/maestras/"),
    window.location.origin,
  );
  tableApiUrl.searchParams.set("tipo", tipo);
  if (requiresWhatsappGroup) {
    tableApiUrl.searchParams.set("en_whatsapp", "true");
  }

  return (
    <>
      {/* Show overlay during initial page load or detail fetch */}
      {(loading || loadingDetail) && <LoadingOverlay />}

      <div className="maestra-page capsule">
        <GenericTable
          apiUrl={tableApiUrl.toString()}
          columns={columns}
          onRowClick={handleMaestraSelect}
          category="maestras"
          title={titleLabel}   
          initialPageIndex={currentPageIndex}
          onPageChange={handlePageChange}
          pageSize={currentPageSize}
          onPageSizeChange={handlePageSizeChange}
        />

        {/* Modal shows only when we have data */}
        {showModal && selectedMaestra && (
          <Modal
            show={showModal}
            size="xl"
            backdrop="static"
            dialogClassName="modal-100w"
            onHide={handleCloseModal}
          >
            <Modal.Body className="maestra-detail-modal position-relative">
              <button
                className="btn btn-sm btn-danger position-absolute top-0 end-0 m-3"
                onClick={handleCloseModal}
              >
                &times;
              </button>
              <MaestrasView maestra={selectedMaestra} />
            </Modal.Body>
          </Modal>
        )}
      </div>
    </>
  );
};

export default MaestraPage;
